"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="17434" className="hidden lg:flex gap-4">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex justify-start max-w-none">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "#"} className="bg-transparent text-xs font-mono font-light tracking-widest text-foreground/70 hover:text-primary transition-colors uppercase">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-card border-b border-border shadow-2xl lg:hidden animate-in slide-in-from-top-5 fade-in duration-200 z-40">
      <nav className="container mx-auto px-6 py-6 flex flex-col gap-4">
        {loading ? <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-6 w-32 bg-muted/20 animate-pulse rounded" />)}
          </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-sm font-mono font-medium tracking-wider text-foreground hover:text-primary transition-colors py-3 border-b border-border/30 last:border-0 uppercase" onClick={onClose} data-index={i}>
              {item.label}
            </Link>)}
        <div className="pt-4">
          <Button className="w-full bg-transparent border border-primary text-primary hover:bg-primary/10 hover:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all font-mono text-xs uppercase tracking-widest rounded-sm" asChild>
            <Link to="/StrategicInquiriesPage" onClick={onClose}>
              Start Assessment
            </Link>
          </Button>
        </div>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-xl shadow-md border-border/40" : "bg-background")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between relative">
        {}
        <div className="flex-1 flex items-center justify-start">
          {}
          <Button variant="ghost" size="icon" className="lg:hidden -ml-2 text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>

          {}
          <MenuProvider menu_id="41">
            <DesktopNav />
          </MenuProvider>
        </div>

        {}
        <div className="flex-0 flex items-center justify-center absolute left-1/2 -translate-x-1/2">
          <Link to="/HomePage" className="flex items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1619657/img/luxury_cruise_vacations_logo.png" alt="Century Pointe Holdings" className="h-7 w-auto object-contain brightness-0 invert" />
          </Link>
        </div>

        {}
        <div className="flex-1 flex items-center justify-end">
          <Button asChild className="hidden lg:inline-flex bg-transparent border border-primary/50 text-primary hover:bg-primary/10 hover:shadow-[0_0_20px_rgba(0,255,255,0.3)] transition-all duration-300 font-mono text-xs uppercase tracking-widest px-6 rounded-sm">
            <Link to="/StrategicInquiriesPage">Start Assessment</Link>
          </Button>
        </div>
      </div>

      {}
      <MenuProvider menu_id="41">
        <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}